import type { Plan, Point } from '@trippath/shared';

/** Where the map's tiles, styles, fonts and sprites are kept on this device. */
export const TILES = 'trippath-tiles';
/** The maps' own styles, light and dark: kept so the map draws with no network. */
export const STYLES = [
  'https://tiles.openfreemap.org/styles/positron',
  'https://tiles.openfreemap.org/styles/liberty',
  'https://tiles.openfreemap.org/styles/dark',
];
/** Where OpenFreeMap says its vector tiles are: their address names this week's build. */
const TILEJSON = 'https://tiles.openfreemap.org/planet';
/** The deepest zoom OpenFreeMap has; the map draws closer from it. */
const DEEPEST = 14;
/** The streets in full only near the middle: a big town's tile at the deepest zoom is ¾ MB. */
const STREETS_KM = 6;
/** A map kept this long is said to be old; it works on until a new one is kept. */
export const OLD_DAYS = 30;

/** A part of the trip kept on the phone: the whole route seen from afar, or a stop's town. */
export interface Area {
  id: string;
  /** The stop it is of; none for the route. */
  stop: string | null;
  name: string;
  /** The stop's first and last day. */
  days: [string, string] | null;
  /** How far round the stop is kept, in kilometres. */
  radiusKm: number;
  /** West, south, east, north. */
  bounds: [number, number, number, number];
  center: Point | null;
}

/** A tile: its zoom and its column and row. */
export interface Tile {
  z: number;
  x: number;
  y: number;
}

/** What is kept of an area on this device. */
export interface Kept {
  tiles: number;
  bytes: number;
  /** When it was kept whole; none while it is loading. */
  at: string | null;
}

/** How this device keeps its maps. */
export interface Settings {
  wifiOnly: boolean;
  autoNew: boolean;
  /** Each stop's radius, when it was changed from the usual. */
  radius: Record<string, number>;
}

/** The radius kept round a stop unless said: its town and the places near. */
export const RADIUS_KM = 25;

/** The tile a point is in at a zoom. */
export function tileOf(point: Point, z: number): [number, number] {
  const n = 2 ** z;
  const lat = Math.max(-85.05, Math.min(85.05, point.lat)) * (Math.PI / 180);
  const x = Math.floor(((point.lon + 180) / 360) * n);
  const y = Math.floor(((1 - Math.log(Math.tan(lat) + 1 / Math.cos(lat)) / Math.PI) / 2) * n);
  return [Math.min(n - 1, Math.max(0, x)), Math.min(n - 1, Math.max(0, y))];
}

/** The box `km` round a point: west, south, east, north. */
export function around(point: Point, km: number): [number, number, number, number] {
  const lat = km / 111.32;
  const lon = km / (111.32 * Math.max(0.01, Math.cos(point.lat * (Math.PI / 180))));
  return [point.lon - lon, point.lat - lat, point.lon + lon, point.lat + lat];
}

/** The tiles that cover a box at each of `zooms`. */
export function tilesIn(bounds: [number, number, number, number], zooms: number[]): Tile[] {
  const [west, south, east, north] = bounds;
  return zooms.flatMap((z) => {
    const [x0, y0] = tileOf({ lat: north, lon: west }, z);
    const [x1, y1] = tileOf({ lat: south, lon: east }, z);
    const found: Tile[] = [];
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) found.push({ z, x, y });
    return found;
  });
}

/** Each tile an area is drawn from: the route to a town's zoom; a stop to its streets near the
 * middle, its roads and places out to its radius. */
export function tilesOf(area: Area): Tile[] {
  if (area.center === null) return tilesIn(area.bounds, [0, 1, 2, 3, 4, 5, 6, 7, 8]);
  const near = around(area.center, Math.min(area.radiusKm, STREETS_KM));
  return [...tilesIn(area.bounds, [9, 10, 11, 12]), ...tilesIn(near, [13, DEEPEST])];
}

/** About how large a tile is at a zoom, in bytes, as OpenFreeMap's were in Asia's towns: a
 * world tile holds the most, the route's finer ones less, a town's grow again to its streets. */
const TILE_BYTES = [
  500_000, 500_000, 500_000, 300_000, 300_000, 300_000, 160_000, 120_000, 80_000, 200_000,
  140_000, 90_000, 90_000, 110_000, 250_000,
];
function tileBytes(z: number): number {
  return TILE_BYTES[z] as number;
}

/** About how large an area is before it is kept. */
export function estimate(area: Area): number {
  return tilesOf(area).reduce((all, tile) => all + tileBytes(tile.z), 0);
}

/** The areas of a trip: its route, then each stop on the map, by its own radius. */
export function areasOf(plan: Plan, radius: Record<string, number>): Area[] {
  const placed = plan.stops.flatMap((stop) =>
    stop.place ? [{ stop, point: stop.place }] : [],
  );
  if (placed.length === 0) return [];
  const lats = placed.map(({ point }) => point.lat);
  const lons = placed.map(({ point }) => point.lon);
  const route: Area = {
    id: 'route',
    stop: null,
    name: '',
    days: null,
    radiusKm: 0,
    bounds: [
      Math.min(...lons) - 1,
      Math.min(...lats) - 1,
      Math.max(...lons) + 1,
      Math.max(...lats) + 1,
    ],
    center: null,
  };
  return [
    route,
    ...placed.map(({ stop, point }) => {
      const km = radius[stop.id] ?? RADIUS_KM;
      return {
        id: stop.id,
        stop: stop.id,
        name: stop.city,
        days: [stop.start, stop.end] as [string, string],
        radiusKm: km,
        bounds: around(point, km),
        center: point,
      };
    }),
  ];
}

/** A tile's address with this week's build left out: the key it is kept under. */
export function tileKey(url: string): string {
  return url.replace(/\/planet\/[^/]+\/(\d+\/\d+\/\d+\.pbf)$/, '/planet/$1');
}

/** The address each tile is asked at this week. */
async function template(): Promise<string> {
  const answer = await fetch(TILEJSON);
  const said = (await answer.json()) as { tiles?: string[] };
  const cache = await caches.open(TILES);
  await cache.put(TILEJSON, new Response(JSON.stringify(said)));
  return said.tiles?.[0] ?? `${TILEJSON}/{z}/{x}/{y}.pbf`;
}

/** The map's styles, their sprites and the fonts' Latin and Cyrillic glyphs, kept. */
async function keepStyles(cache: Cache): Promise<void> {
  const fonts = ['Noto Sans Regular', 'Noto Sans Bold', 'Noto Sans Italic'];
  const ranges = ['0-255', '256-511', '1024-1279', '8192-8447'];
  const urls = [
    ...STYLES,
    'https://tiles.openfreemap.org/sprites/ofm_f384/ofm.json',
    'https://tiles.openfreemap.org/sprites/ofm_f384/ofm.png',
    'https://tiles.openfreemap.org/sprites/ofm_f384/ofm@2x.json',
    'https://tiles.openfreemap.org/sprites/ofm_f384/ofm@2x.png',
    ...fonts.flatMap((font) =>
      ranges.map(
        (range) =>
          `https://tiles.openfreemap.org/fonts/${encodeURIComponent(font)}/${range}.pbf`,
      ),
    ),
  ];
  await Promise.all(
    urls.map(async (url) => {
      const answer = await fetch(url).catch(() => null);
      if (answer?.ok) await cache.put(url, answer);
    }),
  );
}

/**
 * Keeps an area's tiles on this device, six at a time, saying how far it has got; what it kept,
 * or `null` when it was stopped. The styles and fonts are kept with the first area.
 */
export async function keep(
  area: Area,
  told: (done: number, bytes: number, all: number) => void,
  stopped: () => boolean,
): Promise<Kept | null> {
  const tiles = tilesOf(area);
  const cache = await caches.open(TILES);
  const [from] = await Promise.all([template(), keepStyles(cache)]);
  let done = 0;
  let bytes = 0;
  const queue = [...tiles];
  const worker = async (): Promise<void> => {
    for (let tile = queue.shift(); tile !== undefined && !stopped(); tile = queue.shift()) {
      const url = from
        .replace('{z}', String(tile.z))
        .replace('{x}', String(tile.x))
        .replace('{y}', String(tile.y));
      const answer = await fetch(url).catch(() => null);
      if (answer?.ok) {
        const body = await answer.arrayBuffer();
        bytes += body.byteLength;
        await cache.put(tileKey(url), new Response(body, { headers: answer.headers }));
      }
      done += 1;
      told(done, bytes, tiles.length);
    }
  };
  await Promise.all(Array.from({ length: 6 }, worker));
  return stopped() ? null : { tiles: tiles.length, bytes, at: new Date().toISOString() };
}

/** The trip's plan kept where the service worker answers from with no network, so Today knows
 * its places offline though the plan was last read before the worker was in charge. */
export async function keepPlan(trip: string): Promise<void> {
  const url = `/_/api/trips/${trip}`;
  const answer = await fetch(url).catch(() => null);
  if (answer?.ok) {
    // Not kept when the phone has no room: Today says nothing of the map then.
    await caches.open('trippath-api').then(
      (cache) => cache.put(url, answer),
      () => undefined,
    );
  }
}

/** Lets go of an area's tiles that no other kept area of `others` needs. */
export async function forget(area: Area, others: Area[]): Promise<void> {
  const needed = new Set(
    others.flatMap((one) => tilesOf(one).map((t) => `${t.z}/${t.x}/${t.y}`)),
  );
  const cache = await caches.open(TILES);
  await Promise.all(
    tilesOf(area)
      .filter((t) => !needed.has(`${t.z}/${t.x}/${t.y}`))
      .map((t) => cache.delete(`${TILEJSON}/${String(t.z)}/${String(t.x)}/${String(t.y)}.pbf`)),
  );
}

/** What this device keeps of a trip's areas, by area. */
export function keptOf(trip: string): Record<string, Kept> {
  try {
    return JSON.parse(localStorage.getItem(`trippath-offline-${trip}`) ?? '{}') as Record<
      string,
      Kept
    >;
  } catch {
    return {};
  }
}

/** Says what this device keeps of a trip's areas. */
export function setKept(trip: string, kept: Record<string, Kept>): void {
  try {
    localStorage.setItem(`trippath-offline-${trip}`, JSON.stringify(kept));
  } catch {
    // Not kept: the areas are said again as not kept, the tiles are still there.
  }
}

/** How this device keeps its maps. */
export function settings(): Settings {
  const usual: Settings = { wifiOnly: true, autoNew: true, radius: {} };
  try {
    return {
      ...usual,
      ...(JSON.parse(localStorage.getItem('trippath-offline') ?? '{}') as Partial<Settings>),
    };
  } catch {
    return usual;
  }
}

/** Says how this device keeps its maps. */
export function setSettings(next: Settings): void {
  try {
    localStorage.setItem('trippath-offline', JSON.stringify(next));
  } catch {
    // Not kept: the usual next time.
  }
}

/** Whether this device is on a phone's own network now, as far as the browser says. */
export function onCellular(where: Navigator): boolean {
  const connection = (where as Navigator & { connection?: { type?: string } }).connection;
  return connection?.type === 'cellular';
}

/** How many days ago an area was kept. */
export function age(kept: Kept, now: number): number {
  return kept.at === null ? 0 : Math.floor((now - Date.parse(kept.at)) / 86_400_000);
}
